"use client"

import { Trash2, Upload } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import type { Student } from "../../../../lib/students-data"

interface ReportTableRowProps {
  student: Student
  isSelected: boolean
  onSelect: (id: string) => void
  onViewDetails: (student: Student) => void
  onUpload: (id: string) => void
  onDelete: (id: string) => void
}

export function ReportTableRow({
  student,
  isSelected,
  onSelect,
  onViewDetails,
  onUpload,
  onDelete,
}: ReportTableRowProps) {
  const getGradeColor = (gpa: string) => {
    const gpaValue = Number.parseFloat(gpa)
    if (gpaValue >= 4.5) return "bg-green-100 text-green-800"
    if (gpaValue >= 4.0) return "bg-blue-100 text-blue-800"
    if (gpaValue >= 3.5) return "bg-yellow-100 text-yellow-800"
    return "bg-orange-100 text-orange-800"
  }

  // Mock subjects based on career path
  const getSubjects = (careerPath: string) => {
    const subjectMap: Record<string, string> = {
      "Software Engineer": "Math, Physics, CS",
      Medicine: "Biology, Chemistry, Physics",
      Law: "Government, Literature, Economics",
      Architecture: "Tech Drawing, Math, Physics",
      Engineering: "Physics, Chemistry, Math",
      Teaching: "English, Education, Social Studies",
      Business: "Economics, Commerce, Accounting",
      "Arts & Design": "Fine Arts, Literature, Design",
      Agriculture: "Agric Science, Biology, Chemistry",
      Accounting: "Accounting, Economics, Commerce",
    }
    return subjectMap[careerPath] || "N/A"
  }

  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="p-5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(student.id)}
          className="w-4 h-4 text-primary border-border rounded focus:ring-primary cursor-pointer"
        />
      </td>
      <td className="p-5">
        <button
          onClick={() => onViewDetails(student)}
          className="font-bold text-primary hover:text-primary/80 transition-colors text-left"
        >
          {student.name}
        </button>
        <p className="text-xs text-muted-foreground">ID: {student.studentId}</p>
      </td>
      <td className="p-5 text-sm text-foreground">{student.class}</td>
      <td className="p-5 text-sm text-foreground">{student.guardianEmail}</td>
      <td className="p-5">
        <Badge variant="outline" className={`${getGradeColor(student.gpa)}`}>
          {student.gpa} / 5.0
        </Badge>
      </td>
      <td className="p-5 text-sm text-foreground">{getSubjects(student.careerPath)}</td>
      <td className="p-5 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onUpload(student.id)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Upload Results"
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(student.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}
